import React, { useState } from "react";
import { Search, Plus, Edit2, Trash2, X, ChevronsLeft, ChevronsRight } from "lucide-react";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { usePolicyCategories } from "../../context/PolicyCategoryContext";
import { usePolicies } from "../../context/PolicyContext";
import { useAuth } from "../../context/AuthContext";

// Reusable input class so every field shares the same lavender focus style
const inputClass =
  "w-full border border-gray-300 rounded-lg px-3 py-2 outline-none transition-colors duration-150 focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff]/40";

export default function PolicyManagement() {
  const {
  categories: categoryList,
} = usePolicyCategories();

const {
  policies,
  createPolicy,
  updatePolicy,
  deletePolicy,
  updateStatus,
  setPolicies,
} = usePolicies();
const { canDo } = useAuth();

const [statusLoading, setStatusLoading] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [currentPolicy, setCurrentPolicy] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  const [loading, setLoading] = useState(false);

  const statusColors = {
    active: "bg-green-100 text-green-600",
    inactive: "bg-red-100 text-red-600",
  };

  const filteredPolicies = policies.filter((policy) => {
    const matchesStatus = filterStatus === "All" || policy.status === filterStatus;
    const matchesSearch =
      policy.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      policy.category?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      policy.description?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const totalPages = Math.ceil(filteredPolicies.length / itemsPerPage);
  const paginatedPolicies = filteredPolicies.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  const handleSubmit = async (e) => {
  e.preventDefault();

  const form = e.target;

  const title = form.title.value.trim();
  const category_id = form.category_id.value;
  const description = form.description.value.trim();
  const status = form.status.value;

  const userData = JSON.parse(localStorage.getItem("user"));

  if (!title || !category_id || !description) {
    return alert("Please fill all fields");
  }

  setLoading(true);

  try {
    if (currentPolicy) {
      await updatePolicy(currentPolicy.id, {
        title,
        category_id,
        description,
        status,
        update_audit_id: userData?.id,
      });
    } else {
      await createPolicy({
        title,
        category_id,
        description,
        status,
        create_audit_id: userData?.id,
      });
    }

    setShowModal(false);
    setCurrentPolicy(null);
  } catch (err) {
    console.error(err);
  }finally {
    setLoading(false);
  }
};

const handleStatusToggle = async (policy) => {
  const userData = JSON.parse(localStorage.getItem("user"));

  const newStatus =
    policy.status === "active" ? "inactive" : "active";

  setPolicies((prev) =>
    prev.map((p) =>
      p.id === policy.id ? { ...p, status: newStatus } : p
    )
  );

  setStatusLoading(policy.id);

  const res = await updateStatus(policy.id, {
    status: newStatus,
    update_audit_id: userData?.id,
  });

  if (!res.success) {
    // rollback
    setPolicies((prev) =>
      prev.map((p) =>
        p.id === policy.id
          ? { ...p, status: policy.status }
          : p
      )
    );
  }

  setStatusLoading(null);
};

  const handleDelete = async () => {
  try {
    const userData = JSON.parse(localStorage.getItem("user"));

    const res = await deletePolicy(deleteId, {
      delete_audit_id: userData?.id,
    });

    if (res.success) {
      setShowDeleteModal(false);
      setDeleteId(null);
    } else {
      alert(res.message || "Failed to delete policy");
    }
  } catch (err) {
    console.error(err);
    alert("Something went wrong");
  }
};

  return (
    <>
      <div className=" bg-sky-50 ">
        <div className="px-9 py-5 max-md:p-4">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-5">Policy Management</h1>

          {/* Search & Add Section */}
          <div className="flex justify-between items-center flex-wrap gap-3 mb-6 bg-white rounded-xl px-4 py-3 shadow-sm">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center flex-wrap gap-3">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center border border-gray-300 rounded-lg bg-white w-full sm:w-86 md:w-[300px] lg:w-[350px] overflow-hidden transition-colors duration-150 focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
                <div className="flex items-center flex-1 px-3 py-2">
                  <Search className="text-gray-400 h-3 w-3 mr-2" />
                  <input
                    type="text"
                    placeholder="Search policies..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="flex-1 outline-none text-gray-700 bg-transparent"
                  />
                </div>

                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="border-t sm:border-t-0 sm:border-l border-gray-300 px-3 py-2 outline-none bg-white w-full sm:w-auto"
                >
                  <option>All</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              {(searchQuery || filterStatus !== "All") && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery("");
                    setFilterStatus("All");
                  }}
                  className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 transition whitespace-nowrap shrink-0 w-full sm:w-auto"
                >
                  <X size={14} />
                  Clear Filters
                </button>
              )}
            </div>

{canDo("PolicyManagement", "Add") && (
            <button
              onClick={() => {
                setCurrentPolicy(null);
                setShowModal(true);
              }}
              className="flex items-center  text-white px-4 py-2 rounded-lg  transition-colors duration-200 w-full sm:w-auto justify-center"
              style={{
                background: "linear-gradient(135deg, #2563eb, #d12465)",
              }}
            >
              <Plus className="h-4 w-4 mr-1" /> Add New
            </button>
)}
          </div>

          {/* Table Section */}
          <div className="bg-white rounded-xl shadow-md p-4">
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse text-sm">
              <thead className="bg-sky-50 rounded-t-xl">
                <tr>
                  <th className="py-3 px-4 text-left font-semibold text-gray-600">ID</th>
                  <th className="py-3 px-4 text-left font-semibold text-gray-600">Title</th>
                  <th className="py-3 px-4 text-left font-semibold text-gray-600">Category</th>
                  <th className="py-3 px-4 text-left font-semibold text-gray-600">Description</th>
                  <th className="py-3 px-4 text-left font-semibold text-gray-600">Status</th>
                  {(canDo("PolicyManagement", "Edit") ||
  canDo("PolicyManagement", "Delete")) && (
  <th className="py-3 px-4 text-center font-semibold text-gray-600">
    Actions
  </th>
)}
                </tr>
              </thead>
              <tbody>
                {paginatedPolicies.length > 0 ? (
                  paginatedPolicies.map((policy, index) => (
                    <tr
                      key={policy.id}
                      className={`${index % 2 !== 0 ? "bg-[#f7f6fe]" : ""} border-b hover:bg-gray-50`}
                    >
                      <td className="py-3 px-4 text-sky-600 whitespace-nowrap">
                        {(index + 1).toString().padStart(2, "0")}
                      </td>
                      <td className="py-3 px-4 whitespace-nowrap">{policy.title}</td>
                      <td className="py-3 px-4 whitespace-nowrap">{policy.category_name}</td>
                      <td className="py-3 px-4 min-w-[200px]">{policy.description}</td>
                      <td className="py-3 px-4">
                        <span
                          onClick={() =>
                            statusLoading !== policy.id &&
                            handleStatusToggle(policy)
                          }
                          className={`px-3 py-1 rounded-full text-xs font-medium cursor-pointer transition-all ${
                            statusColors[policy.status]
                          } ${
                            statusLoading === policy.id
                              ? "opacity-50"
                              : "hover:scale-105"
                          }`}
                        >
                          {statusLoading === policy.id
                            ? "Updating..."
                            : policy.status}
                        </span>
                      </td>
                      {(canDo("PolicyManagement", "Edit") ||
  canDo("PolicyManagement", "Delete")) && (
                      <td className="py-3 px-4 flex justify-center space-x-2 text-gray-600">
                        {canDo("PolicyManagement", "Edit") && (
                        <Edit2
                          className="h-4 w-4 cursor-pointer hover:text-sky-600"
                          onClick={() => {
                            setCurrentPolicy(policy);
                            setShowModal(true);
                          }}
                        />
                        )}
                        {canDo("PolicyManagement", "Delete") && (
                        <Trash2
                          className="h-4 w-4 cursor-pointer hover:text-red-600"
                          onClick={() => {
                            setDeleteId(policy.id);
                            setShowDeleteModal(true);
                          }}
                        />
                        )}
                      </td>
  )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-6 text-gray-500">
                      No policies found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-3">
            <div className="flex items-center space-x-2">
              <label htmlFor="recordsPerPage" className="text-gray-700 text-sm">Records:</label>
              <select
                id="recordsPerPage"
                className="border border-gray-300 rounded-md px-2 py-1 text-sm outline-none transition-colors duration-150 focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff]/40"
                value={itemsPerPage}
                onChange={(e) => {
                  setPage(1);
                  setItemsPerPage(Number(e.target.value));
                }}
              >
                {[3, 5, 10, 20].map((num) => (
                  <option key={num} value={num}>
                    {num}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center space-x-1">
              <button
                onClick={() => setPage(1)}
                disabled={page === 1}
                className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronsLeft size={20} />
              </button>
              <button
                className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                disabled={page === 1}
              >
                <MdKeyboardArrowLeft size={20} />
              </button>

              {(() => {
                let startPage = Math.max(page - 1, 1);
                let endPage = startPage + 2;
                if (endPage > totalPages) {
                  endPage = totalPages;
                  startPage = Math.max(endPage - 2, 1);
                }
                const pages = [];
                for (let i = startPage; i <= endPage; i++) {
                  pages.push(
                    <button
                      key={i}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        i === page
                          ? "text-white bg-[linear-gradient(135deg,#2563eb,#d12465)]"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                      onClick={() => setPage(i)}
                    >
                      {i}
                    </button>
                  );
                }
                return pages;
              })()}

              <button
                className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={page === totalPages}
              >
                <MdKeyboardArrowRight size={20} />
              </button>
              <button
                onClick={() => setPage(totalPages)}
                disabled={page === totalPages}
                className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronsRight size={20} />
              </button>
            </div>
          </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-6 relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => {
                setShowModal(false);
                setCurrentPolicy(null);
              }}
              className="absolute right-4 top-4 text-gray-500 hover:text-gray-700"
            >
              <X className="h-5 w-5" />
            </button>
            <h3 className="text-xl font-semibold mb-4">
              {currentPolicy ? "Edit Policy" : "Add New Policy"}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Title</label>
                <input
                  name="title"
                  type="text"
                  defaultValue={currentPolicy?.title || ""}
                  placeholder="Enter Policy Title"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Category</label>
                <select
                  name="category_id"
                  defaultValue={currentPolicy?.category_id || ""}
                  className={inputClass}
                >
                  <option value="">Select Category</option>

                  {categoryList.filter((cat) => cat.status === 'active').map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.category_name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  name="description"
                  rows="3"
                  defaultValue={currentPolicy?.description || ""}
                  placeholder="Description"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Status</label>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="status"
                      value="active"
                      defaultChecked={currentPolicy?.status === "active" || !currentPolicy}
                      className="accent-[#8c57ff]"
                    />
                    <span>Active</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="status"
                      value="inactive"
                      defaultChecked={currentPolicy?.status === "inactive"}
                      className="accent-[#8c57ff]"
                    />
                    <span>Inactive</span>
                  </label>
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 rounded-lg border"
                >
                  Cancel
                </button>
                <button
  type="submit"
  disabled={loading}
  className="px-4 py-2 rounded-lg text-white disabled:opacity-60 disabled:cursor-not-allowed"
  style={{
    background: "linear-gradient(135deg, #2563eb, #d12465)",
  }}
>
  {loading
    ? "Processing..."
    : currentPolicy
    ? "Update Policy"
    : "Create New Policy"}
</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-sm p-6 text-center">
            <h3 className="text-lg font-semibold mb-3">Delete Policy?</h3>
            <p className="text-gray-600 mb-5">
              Are you sure you want to delete this policy? This action cannot be undone.
            </p>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 rounded-lg border"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}